"use client";

import * as React from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Search, ShoppingBag, Menu, User, Sprout } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobileNavigation } from "./MobileNavigationContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface NavItem {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path?: string;
  action?: "menu" | "search";
  testId: string;
}

const navItems: NavItem[] = [
  {
    id: "home",
    icon: Home,
    label: "Home",
    path: "/",
    testId: "bottom-nav-home",
  },
  {
    id: "search",
    icon: Search,
    label: "Search",
    action: "search",
    testId: "bottom-nav-search",
  },
  {
    id: "design",
    icon: Sprout,
    label: "Design",
    path: "/checkout",
    testId: "bottom-nav-design",
  },
  {
    id: "portal",
    icon: User,
    label: "Portal",
    path: "/portal",
    testId: "bottom-nav-portal",
  },
  {
    id: "menu",
    icon: Menu,
    label: "More",
    action: "menu",
    testId: "bottom-nav-menu",
  },
];

export function BottomNavBar() {
  const isMobile = useIsMobile();
  const [location, setLocation] = useLocation();
  const { toggleMenu, toggleSearch, isMenuOpen, isSearchOpen } = useMobileNavigation();
  const [activeItem, setActiveItem] = React.useState<string | null>(null);

  // Haptic feedback helper
  const triggerHaptic = React.useCallback(() => {
    if ("vibrate" in navigator) {
      navigator.vibrate(10);
    }
  }, []);

  const handleNavClick = React.useCallback(
    (item: NavItem) => {
      triggerHaptic();
      setActiveItem(item.id);

      if (item.action === "menu") {
        toggleMenu();
      } else if (item.action === "search") {
        toggleSearch();
      } else if (item.path) {
        setLocation(item.path);
      }

      // Reset active state after animation
      setTimeout(() => setActiveItem(null), 150);
    },
    [toggleMenu, toggleSearch, setLocation, triggerHaptic]
  );

  const isActive = React.useCallback(
    (item: NavItem) => {
      if (item.action === "menu") return isMenuOpen;
      if (item.action === "search") return isSearchOpen;
      if (item.path === "/") return location === "/";
      if (item.path === "/portal") return location.startsWith("/portal");
      return item.path === location;
    },
    [location, isMenuOpen, isSearchOpen]
  );

  // Don't render on desktop
  if (!isMobile) return null;

  return (
    <>
      {/* Safe area spacer to prevent content from being hidden behind the nav bar */}
      <div className="h-20 md:hidden" aria-hidden="true" />

      {/* Bottom Navigation Bar */}
      <nav
        className={cn(
          "fixed bottom-0 left-0 right-0 z-50 md:hidden",
          "bg-background/95 backdrop-blur-lg border-t border-border",
          "safe-area-bottom"
        )}
        role="navigation"
        aria-label="Main navigation"
        data-testid="bottom-nav-bar"
      >
        {/* Navigation Items - positioned in thumb zone */}
        <div className="flex items-stretch justify-around h-16 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const pressed = activeItem === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 relative",
                  "min-w-[64px] min-h-[48px]", // Minimum touch target size
                  "transition-all duration-150 ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  pressed && "scale-95"
                )}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                data-testid={item.testId}
              >
                {/* Active indicator */}
                <AnimatePresence>
                  {active && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      className="absolute inset-x-3 -top-0.5 h-0.5 bg-primary rounded-full"
                    />
                  )}
                </AnimatePresence>

                {/* Icon with animation */}
                <motion.div
                  animate={{
                    scale: active ? 1.1 : 1,
                    y: active ? -2 : 0,
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Icon
                    className={cn(
                      "w-6 h-6 transition-colors duration-150",
                      active ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                </motion.div>

                {/* Label */}
                <span
                  className={cn(
                    "text-[10px] mt-1 font-medium transition-colors duration-150",
                    active ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {item.label}
                </span>

                {/* Ripple effect on press */}
                <AnimatePresence>
                  {pressed && (
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0.5 }}
                      animate={{ scale: 2, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0 bg-primary/20 rounded-full"
                    />
                  )}
                </AnimatePresence>
              </button>
            );
          })}
        </div>

        {/* Home indicator safe area for iOS */}
        <div className="h-safe-area-inset-bottom bg-background" />
      </nav>
    </>
  );
}

export default BottomNavBar;
